/*
  CustomBackendRecognizer: streams audio to backend via WebSocket, backend handles Azure Speech SDK.
  Frontend captures audio → WebSocket → Backend (Azure SDK) → WebSocket results → Frontend
  Compatible with iPad and browsers with limited Azure SDK support.
*/

import type { SocketEvent } from '@trinity/chat';
import { SocketNamespace } from '@trinity/chat';
import type { Socket } from 'socket.io-client';
import { getSocket } from '@trinity/chat-components/websocket/socket';
import { createLogger } from '@trinity/chat-components/websocket/utils';
import { CustomEventTarget } from '../../speech-recognition-aws-polyfill/lib/CustomEventTarget';

export class CustomBackendRecognizer
  extends CustomEventTarget
  implements SpeechRecognition {
  static type = 'CUSTOM_BACKEND';
  static isSupported = !!navigator?.mediaDevices?.getUserMedia;

  public listening = false;
  public lang = 'en-US';
  public continuous = false;
  private socket: Socket | null = null;
  private chatIdentifier: string = '';
  private mediaRecorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private config: { chatIdentifier: string; lang?: string; continuous?: boolean };
  private frontendTimeoutId: number | null = null;
  private hasReceivedTranscript: boolean = false;
  private audioContext: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;

  private logger = createLogger({
    namespace: SocketNamespace.Speech,
  });

  // Required by SpeechRecognition interface
  private _interimResults = false;
  set interimResults(val: boolean) {
    this._interimResults = !!val;
  }
  get interimResults() {
    return this._interimResults;
  }

  set maxAlternatives(_: number) {
    /* not implemented */
  }
  get maxAlternatives() {
    return 1;
  }

  set grammars(_: SpeechGrammarList) {
    /* not implemented */
  }
  get grammars() {
    return window.SpeechGrammarList
      ? new window.SpeechGrammarList()
      : ([] as unknown as SpeechGrammarList);
  }

  constructor(config: { chatIdentifier?: string; lang?: string; continuous?: boolean } = { lang: 'en-US', continuous: false }) {
    super();

    this.config = {
      chatIdentifier: config.chatIdentifier || '',
      lang: config?.lang ?? 'en-US',
      continuous: config?.continuous ?? false,
    };
    this.lang = this.config.lang;
    this.continuous = this.config.continuous;
    this.chatIdentifier = this.config.chatIdentifier;
  }

  private logSocketEvents(event: SocketEvent, payload: unknown) {
    if (!this.logger) {
      this.logger = createLogger({
        namespace: SocketNamespace.Speech,
      });
    }
    this.logger.event(event, payload);
  }

  async start() {
    if (this.listening) {
      console.warn(
        '[CustomBackendRecognizer] Already listening, ignoring start request',
      );
      return;
    }

    this.dispatchEvent(new Event('start'));

    try {
      // Set up WebSocket connection to the speech namespace
      this.socket = getSocket({
        namespace: SocketNamespace.Speech,
        baseUrl: window.location.origin,
        socketPath: '/socket.io',
      });

      // Test connection
      this.socket.emit('test-connection');

      this.socket.on('transcript', this.handleTranscript.bind(this));
      this.socket.on('error', this.handleError.bind(this));
      this.socket.on(
        'transcription-complete',
        this.handleTranscriptionComplete.bind(this),
      );
      this.socket.on('session-ended', this.handleSessionEnded.bind(this));

      this.socket.prependAny(this.logSocketEvents);

      this.socket.emit('subscribe', this.chatIdentifier);
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      this.dispatchEvent(new Event('audiostart'));

      // Set up MediaRecorder - prioritize formats that work with Azure
      const mimeTypes = [
        'audio/wav', // Best for Azure Speech Service
        'audio/webm;codecs=pcm', // PCM in WebM container - better than Opus
        'audio/ogg;codecs=opus', // OGG might work better than WebM
        'audio/mp4;codecs=mp4a.40.2', // AAC in MP4 - might be supported
        'audio/webm;codecs=opus', // Fallback for broader compatibility
        'audio/webm', // Generic WebM
        'audio/mp4', // MP4 fallback
      ];

      let selectedMimeType = '';
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }

      if (!selectedMimeType) {
        this.setupWebAudioRecording();
        return;
      }

      if (!selectedMimeType.includes('wav')) {
        this.setupWebAudioRecording();
        return;
      }

      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType: selectedMimeType,
        audioBitsPerSecond: 128000,
      });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0 && this.socket) {
          event.data.arrayBuffer().then((buffer) => {
            if (this.socket) {
              this.socket.emit('audio-chunk', buffer);
            }
          });
        }
      };

      this.mediaRecorder.onstart = () => {
        this.listening = true;
      };

      this.mediaRecorder.onstop = () => {
        // MediaRecorder stopped
      };

      this.mediaRecorder.start(100); // Send chunks every 100ms

      this.frontendTimeoutId = window.setTimeout(() => {
        if (this.listening) {
          this.handleTranscriptionComplete({
            finalTranscript: '',
            timestamp: new Date().toISOString(),
            reason: 'frontend_timeout',
          });
        }
      }, 60000); // 60 seconds frontend timeout
    } catch (err) {
      console.error(
        '[CustomBackendRecognizer] Failed to start recognition:',
        err,
      );
      this.dispatchEvent(new Event('error'));
      this.immediateCleanup();
    }
  }

  stop() {
    this.immediateCleanup();

    if (this.socket) {
      this.socket.emit('audio-end');
    }
  }

  private immediateCleanup() {
    // Reset listening state FIRST before any other cleanup
    const wasListening = this.listening;
    this.listening = false;

    if (this.frontendTimeoutId) {
      clearTimeout(this.frontendTimeoutId);
      this.frontendTimeoutId = null;
    }

    this.hasReceivedTranscript = false;

    if (this.mediaRecorder) {
      if (this.mediaRecorder.state === 'recording') {
        this.mediaRecorder.stop();
      }
      this.mediaRecorder = null;
    }

    if (this.processor) {
      this.processor.disconnect();
      this.processor = null;
    }

    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        track.stop();
      });
      this.stream = null;
    }

    // Disconnect socket BEFORE dispatching events
    if (this.socket) {
      this.socket.emit('unsubscribe', this.chatIdentifier);
      this.socket.off('transcript');
      this.socket.off('error');
      this.socket.off('transcription-complete');
      this.socket.off('session-ended');
      this.socket = null;
    }

    // Only dispatch events if we were actually listening
    if (wasListening) {
      this.dispatchEvent(new Event('audioend'));
      this.dispatchEvent(new Event('end'));
    }
  }

  abort() {
    if (this.frontendTimeoutId) {
      clearTimeout(this.frontendTimeoutId);
      this.frontendTimeoutId = null;
    }

    // Complete cleanup with socket disconnection
    this.immediateCleanup();
  }

  private setupWebAudioRecording() {
    try {
      const AudioContextClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;

      this.audioContext = new AudioContextClass({
        sampleRate: 16000, // Azure preferred sample rate
      });

      this.source = this.audioContext.createMediaStreamSource(this.stream);
      this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);

      let audioChunks: Float32Array[] = [];

      this.processor.onaudioprocess = (event) => {
        if (!this.listening || !this.socket) return;

        const inputData = event.inputBuffer.getChannelData(0);

        audioChunks.push(new Float32Array(inputData));

        if (audioChunks.length >= 4) {
          const combinedLength = audioChunks.reduce(
            (sum, chunk) => sum + chunk.length,
            0,
          );
          const combinedAudio = new Float32Array(combinedLength);

          let offset = 0;
          for (const chunk of audioChunks) {
            combinedAudio.set(chunk, offset);
            offset += chunk.length;
          }

          const wavBuffer = this.createWavBuffer(
            combinedAudio,
            this.audioContext.sampleRate,
            1,
          );

          this.socket.emit('audio-chunk', wavBuffer);

          audioChunks = [];
        }
      };

      this.source.connect(this.processor);
      this.processor.connect(this.audioContext.destination);

      this.listening = true;

      this.frontendTimeoutId = window.setTimeout(() => {
        if (this.listening) {
          this.handleTranscriptionComplete({
            finalTranscript: '',
            timestamp: new Date().toISOString(),
            reason: 'frontend_timeout',
          });
        }
      }, 60000);
    } catch (err) {
      console.error(
        '[CustomBackendRecognizer] Failed to setup Web Audio API:',
        err,
      );
      throw err;
    }
  }

  private createWavBuffer(
    audioData: Float32Array,
    sampleRate: number,
    channels: number,
  ): ArrayBuffer {
    const pcmData = new Int16Array(audioData.length);
    for (let i = 0; i < audioData.length; i++) {
      const sample = Math.max(-1, Math.min(1, audioData[i]));
      pcmData[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
    }

    const byteRate = sampleRate * channels * 2; // 2 bytes per sample for 16-bit
    const blockAlign = channels * 2;
    const dataSize = pcmData.length * 2;
    const fileSize = 36 + dataSize;

    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    // WAV file header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF'); // ChunkID
    view.setUint32(4, fileSize, true); // ChunkSize
    writeString(8, 'WAVE'); // Format
    writeString(12, 'fmt '); // Subchunk1ID
    view.setUint32(16, 16, true); // Subchunk1Size
    view.setUint16(20, 1, true); // AudioFormat (PCM)
    view.setUint16(22, channels, true); // NumChannels
    view.setUint32(24, sampleRate, true); // SampleRate
    view.setUint32(28, byteRate, true); // ByteRate
    view.setUint16(32, blockAlign, true); // BlockAlign
    view.setUint16(34, 16, true); // BitsPerSample
    writeString(36, 'data'); // Subchunk2ID
    view.setUint32(40, dataSize, true); // Subchunk2Size

    // PCM data
    let offset = 44;
    for (let i = 0; i < pcmData.length; i++) {
      view.setInt16(offset, pcmData[i], true);
      offset += 2;
    }

    return buffer;
  }

  private handleTranscript(data: { transcript: string; isFinal: boolean }) {
    if (this.frontendTimeoutId) {
      clearTimeout(this.frontendTimeoutId);
      this.frontendTimeoutId = null;
    }

    this.hasReceivedTranscript = true;

    if (data.transcript) {
      this.emitResult(data.transcript, data.isFinal, 1.0);
    }
  }

  private handleError(data: { error: string }) {
    console.error('[CustomBackendRecognizer] Error from backend:', data.error);
    this.dispatchEvent(new Event('error'));
  }

  private handleTranscriptionComplete(_data: {
    finalTranscript?: string;
    timestamp: string;
    reason?: string;
  }) {
    if (this.frontendTimeoutId) {
      clearTimeout(this.frontendTimeoutId);
      this.frontendTimeoutId = null;
    }

    // Don't abort immediately - let the backend's 10-second timeout handle session end
    // if (this.listening) {
    //   this.abort();
    // }
  }

  private handleSessionEnded(_data: { sessionId: string; timestamp: string }) {
    if (this.listening) {
      this.immediateCleanup();
    }
  }

  private emitResult(
    transcript: string,
    isFinal: boolean,
    _confidence: number = 1.0,
  ) {
    if (transcript && transcript.length > 0) {
      // Use the same format as AWS recognizer for compatibility with react-speech-recognition
      const results = [
        {
          0: {
            transcript,
            confidence: _confidence,
          },
          isFinal,
        },
      ];

      const customEvent = {
        type: 'result',
        results: results,
        defaultPrevented: false,
      };

      this.dispatchEvent(customEvent as unknown as Event);

      if (this.onresult) {
        this.onresult(customEvent as unknown as SpeechRecognitionEvent);
      }

      // DON'T stop on isFinal - let the backend's 10-second silence timeout handle session end
      // if (!this.continuous && isFinal && this.listening) {
      //   console.log('[CustomBackendRecognizer] Stopping because non-continuous and isFinal');
      //   this.stop();
      // }

      // DON'T dispatch 'end' event on isFinal - this triggers react-speech-recognition to stop
      // The 'end' event should only be dispatched when the session actually ends
      // if (isFinal) {
      //   this.dispatchEvent(new Event('end'));
      //   if (this.onend) {
      //     this.onend(new Event('end'));
      //   }
      // }
    } else {
      this.dispatchEvent(new Event('nomatch'));
    }
  }

  set onaudiostart(fn) {
    this.addEventListener('audiostart', fn);
  }
  set onaudioend(fn) {
    this.addEventListener('audioend', fn);
  }
  set onend(fn) {
    this.addEventListener('end', fn);
  }
  set onerror(fn) {
    this.addEventListener('error', fn);
  }
  set onnomatch(fn) {
    this.addEventListener('nomatch', fn);
  }
  set onresult(fn) {
    this.addEventListener('result', fn);
  }
  set onsoundstart(fn) {
    this.addEventListener('soundstart', fn);
  }
  set onsoundend(fn) {
    this.addEventListener('soundend', fn);
  }
  set onspeechstart(fn) {
    this.addEventListener('speechstart', fn);
  }
  set onspeechend(fn) {
    this.addEventListener('speechend', fn);
  }
  set onstart(fn) {
    this.addEventListener('start', fn);
  }
}

export const createCustomBackendSpeechRecognitionPonyfill = (
  config: { chatIdentifier: string; lang?: string; continuous?: boolean },
): typeof CustomBackendRecognizer => {
  // Return a class that always uses the provided config
  class CustomBackendRecognizerWithConfig extends CustomBackendRecognizer {
    constructor() {
      super(config);
    }
  }
  return CustomBackendRecognizerWithConfig;
};

export default CustomBackendRecognizer;
